import React from 'react';
import {Link} from 'react-router-dom';
import {motion} from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const mediaBase = 'https://media-manager-c.questera.ai/greta-media/e842e0d41a21d55020287eff0799ef371d19c38aa92b057bb3ca6b830a67458ee61195b657411aeb9534fd09dfbbea8f/images/aW1hZ2UvanBlZw==/';
const logoFiles = [
  ['City of Dallas', 'a4db24ffe0f27ef2b8f5e26a9e9aa413.jpg'],
  ['FEMA', '579121fa5270069be4e9de50e4320a29.jpg'],
  ['Dolphin Energy', '7500f8cc7290927bddca7cbffc7edc58.jpg'],
  ['ABAA', '2dcd897f88c85ab0e1341b2d9347ce51.jpg'],
  ['Suez Electric', '3ba59fb72121a29c3dca768d7893b1ab.jpg'],
  ['Belco', 'f3cc31fdd15290494bbc9e5d5f52c9ff.jpg'],
  ['Nevis Island Administration', '0c7b938eabca7861de2cd441596f97b8.jpg'],
  ['GPL', '94cf7cd53cd688c74a304e86c9fd68c3.jpg'],
  ['Jamaica Customs Agency', '19ed39e8b2efee8f5d134d809d46a701.jpg'],
  ['Texas Department of Public Safety', '31b57339b4977771e70f4da41e17ec26.jpg'],
  ['ConocoPhillips', 'c84cbcc386df2edeaaa213fd1a245e9c.jpg'],
  ['Hagerty', '895400e7117197ca4447b1610ea429e7.jpg'],
  ['Texas State Firefighters', '61304f29915d63d773293ecd6a76b8e4.jpg'],
  ['Texas TAFE', '704c026bd2976f6c496f06361cf9888a.jpg']
];
const logos = logoFiles.map(([name, file]) => ({name, url: `${mediaBase}${file}`}));
const challenges = [
  ['Complex compliance requirements', FiIcons.FiShield],
  ['Limited safety budgets', FiIcons.FiTrendingDown],
  ['Changing, multi-generational workforces', FiIcons.FiUsers],
  ['New risks: AI, automation, severe weather', FiIcons.FiCpu],
  ['Outdated or generic emergency plans', FiIcons.FiFileMinus],
  ['Weak or untested exercise programs', FiIcons.FiActivity],
  ['Specialized technical skill gaps', FiIcons.FiTarget],
  ['Proving the ROI of preparedness', FiIcons.FiPieChart],
  ['Poor coordination with local agencies', FiIcons.FiRadio]
];
const services = [
  ['Emergency Management & Planning', '/services/emergency-planning', 'EAPs, ERPs, BCPs, and EOC procedures customized to your operations and hazards.', FiIcons.FiFileText],
  ['Compliance & Safety Consulting', '/services/compliance-consulting', 'Gap assessments, audit preparation, and multi-site reviews for OSHA, EPA, and NFPA alignment.', FiIcons.FiCheckSquare],
  ['Drills & Exercises', '/services/drills-exercises', 'HSEEP-aligned drills, tabletops, and full-scale exercises that test decisions, not just optics.', FiIcons.FiActivity],
  ['Training & Development', '/services/training-development', 'Custom course development, train-the-trainer programs, and facility-specific scenarios.', FiIcons.FiTarget],
  ['Leadership Development', '/services/leadership-development', 'Front-line supervisor academies, executive coaching, and emotional intelligence training.', FiIcons.FiUsers]
];
const stats = [
  ['19', 'Years in Business', FiIcons.FiClock],
  ['30+', 'Countries Served', FiIcons.FiGlobe],
  ['9,600+', 'Responses', FiIcons.FiActivity],
  ['NFPA', 'Aligned', FiIcons.FiAward]
];

function TrainingBadge({src, alt, delay, side}) {
  return (
    <motion.div
      className={`absolute -bottom-8 lg:-bottom-12 ${side === 'left' ? '-left-4 lg:-left-12' : '-right-4 lg:-right-12'} bg-white/10 backdrop-blur-md p-4 lg:p-6 rounded-2xl shadow-2xl z-30 border border-white/20 w-32 h-32 md:w-40 md:h-40 flex items-center justify-center`}
      initial={{opacity: 0, y: 20}}
      animate={{opacity: 1, y: 0}}
      transition={{duration: 0.6, delay}}
    >
      <img src={src} alt={alt} className="w-full h-full object-contain drop-shadow-xl" />
    </motion.div>
  );
}

function Hero() {
  return (
    <section className="pt-28 pb-10 px-4 sm:px-6 lg:px-8 max-w-[96rem] mx-auto">
      <div className="relative bg-[#0B1121] rounded-[2rem] lg:rounded-[3rem] shadow-2xl">
        <div className="absolute inset-0 rounded-[2rem] lg:rounded-[3rem] overflow-hidden pointer-events-none">
          <div className="absolute -top-[10%] -left-[5%] w-[45%] h-[50%] bg-brand-green/15 blur-[100px] rounded-full" />
          <div className="absolute bottom-[10%] -right-[5%] w-[35%] h-[50%] bg-blue-500/15 blur-[100px] rounded-full" />
        </div>
        <div className="relative z-10 px-6 sm:px-10 lg:px-16 py-12 lg:py-20">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div className="text-left">
              <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-white/5 border border-white/10 text-slate-300 text-xs font-medium mb-6 backdrop-blur-md">
                <span className="w-2 h-2 rounded-full bg-brand-green" />
                Global Emergency Management Consulting
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white tracking-tight leading-[1.1] mb-6 text-balance">
                Preparedness built for the <span className="text-transparent bg-clip-text bg-gradient-to-r from-brand-green to-emerald-400">real world.</span>
              </h1>
              <p className="text-base lg:text-lg text-slate-400 mb-8 leading-relaxed font-medium max-w-xl">
                We design practical emergency plans, deliver expert training, and build resilient leaders for high-risk organizations worldwide.
              </p>
              <div className="flex flex-col sm:flex-row items-center gap-4 mb-12">
                <Link to="/consultation" className="w-full sm:w-auto px-8 py-4 rounded-full bg-white text-slate-900 font-bold text-sm hover:bg-slate-100 transition-all flex items-center justify-center gap-2 shadow-xl shadow-white/10">
                  Schedule Consultation <SafeIcon icon={FiIcons.FiArrowRight} />
                </Link>
                <Link to="/services" className="w-full sm:w-auto px-8 py-4 rounded-full bg-white/5 text-white font-semibold text-sm border border-white/10 hover:bg-white/10 transition-all backdrop-blur-sm text-center">
                  Explore Services
                </Link>
              </div>
              <div className="grid grid-cols-2 gap-6 pt-8 border-t border-white/10">
                {stats.map(([number, label, icon]) => (
                  <div key={label} className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-xl bg-white/5 border border-white/5 flex items-center justify-center text-brand-green shrink-0">
                      <SafeIcon icon={icon} className="text-lg" />
                    </div>
                    <div>
                      <div className="text-xl font-black text-white leading-none mb-1 tracking-tight">{number}</div>
                      <div className="text-xs text-slate-400 font-bold uppercase tracking-wider leading-tight">{label}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative mt-12 mb-8 lg:my-0">
              <motion.div initial={{opacity: 0, scale: 0.95}} animate={{opacity: 1, scale: 1}} transition={{duration: 0.6, delay: 0.2}} className="relative w-full aspect-[4/3] lg:aspect-square rounded-3xl overflow-hidden shadow-2xl z-10">
                <img src={`${mediaBase}897e6d024bcf28fac2185fea40fffa36.jpg`} alt="Firefighter during emergency response operations" className="w-full h-full object-cover" loading="eager" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0B1121]/80 via-transparent to-transparent opacity-80 pointer-events-none" />
              </motion.div>
              <motion.div className="absolute -top-8 lg:-top-12 -right-4 lg:-right-10 bg-white p-3 rounded-2xl shadow-2xl z-20 w-36 md:w-48 lg:w-56 border border-slate-100" initial={{opacity: 0, scale: 0.8}} animate={{opacity: 1, scale: 1}} transition={{duration: 0.6, delay: 0.6}}>
                <img src="https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1785703678662-image.png" alt="Preparedness Cycle" className="w-full h-auto rounded-xl object-contain" />
              </motion.div>
              <TrainingBadge src="https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1785704551787-image.png" alt="NFPA Authorized Training Provider" delay={0.5} side="left" />
              <TrainingBadge src="https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1785704981916-image.png" alt="NFPA Approved Training Instructor" delay={0.7} side="right" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function LogoCarousel() {
  return (
    <section className="py-10 bg-brand-light">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <p className="text-center text-xs font-bold text-slate-400 uppercase tracking-widest mb-8">Trusted by industry leaders worldwide</p>
        <div className="relative w-full flex overflow-hidden">
          <div className="absolute inset-y-0 left-0 w-24 bg-gradient-to-r from-brand-light to-transparent z-10 pointer-events-none" />
          <div className="absolute inset-y-0 right-0 w-24 bg-gradient-to-l from-brand-light to-transparent z-10 pointer-events-none" />
          <motion.div className="flex gap-12 md:gap-24 items-center w-max px-8" animate={{x: ['0%', '-50%']}} transition={{repeat: Infinity, ease: 'linear', duration: 35}}>
            {[...logos, ...logos, ...logos, ...logos].map((logo, index) => (
              <div key={`${logo.name}-${index}`} className="flex items-center justify-center w-28 h-12 md:w-36 md:h-16 grayscale opacity-50 hover:grayscale-0 hover:opacity-100 transition-all duration-300">
                <img src={logo.url} alt={logo.name} className="max-w-full max-h-full object-contain mix-blend-multiply" loading="lazy" />
              </div>
            ))}
          </motion.div>
        </div>
      </div>
    </section>
  );
}

function Challenges() {
  return (
    <section className="py-24 bg-white border-t border-slate-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-[1fr_0.9fr] gap-10 lg:gap-16 items-center mb-16">
          <div>
            <h2 className="text-brand-green font-bold tracking-widest uppercase text-xs mb-3">The Reality of Readiness</h2>
            <h3 className="text-3xl md:text-5xl font-bold text-brand-navy mb-6 tracking-tight">Are you facing these challenges?</h3>
            <p className="text-lg text-slate-600 font-medium leading-relaxed">
              Modern organizations face complex, overlapping risks. Generic plans and checkbox training are no longer enough to protect your people and maintain operations.
            </p>
          </div>
          <div className="relative h-64 sm:h-80 lg:h-96 rounded-3xl overflow-hidden shadow-xl">
            <img
              src="https://images.unsplash.com/photo-1581092160607-ee22621dd758?auto=format&amp;fit=crop&amp;w=1200&amp;q=85"
              alt="Safety professionals reviewing operational plans at an industrial facility"
              className="w-full h-full object-cover"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-brand-navy/40 to-transparent pointer-events-none" />
          </div>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {challenges.map(([title, icon]) => (
            <div key={title} className="group flex items-start gap-4 p-6 bg-brand-light rounded-2xl border border-slate-200 shadow-sm hover:shadow-md hover:border-brand-green/50 transition-all duration-300">
              <div className="mt-0.5 p-2.5 bg-white text-brand-navy rounded-xl group-hover:bg-brand-navy group-hover:text-white border border-slate-100 transition-colors">
                <SafeIcon icon={icon} className="text-lg" />
              </div>
              <span className="text-slate-700 font-semibold leading-snug pt-1">{title}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Services() {
  return (
    <section className="py-24 bg-brand-light border-y border-slate-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-16 gap-6">
          <div className="max-w-2xl">
            <h2 className="text-brand-green font-bold tracking-widest uppercase text-xs mb-3">Capabilities</h2>
            <h3 className="text-3xl md:text-5xl font-bold text-brand-navy mb-4 tracking-tight">One Connected System</h3>
            <p className="text-lg text-slate-600 font-medium">Start with one service or build a full program. Our solutions integrate seamlessly to build true organizational resilience.</p>
          </div>
          <Link to="/services" className="inline-flex items-center gap-2 font-bold text-brand-navy bg-white px-6 py-3 rounded-xl hover:bg-slate-50 transition-colors border border-slate-200 shrink-0 shadow-sm">
            View Methodology <SafeIcon icon={FiIcons.FiArrowRight} className="text-brand-green" />
          </Link>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map(([title, path, description, icon]) => (
            <Link key={path} to={path} className="group bg-white rounded-3xl p-8 shadow-sm hover:shadow-xl transition-all duration-300 border border-slate-200 flex flex-col h-full hover:border-brand-green relative overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-brand-green/5 rounded-bl-full -mr-10 -mt-10 transition-transform group-hover:scale-110" />
              <div className="w-14 h-14 bg-brand-light text-brand-navy rounded-2xl shadow-sm border border-slate-100 flex items-center justify-center mb-6 group-hover:bg-brand-navy group-hover:text-white transition-colors relative z-10">
                <SafeIcon icon={icon} className="text-2xl" />
              </div>
              <h4 className="text-xl font-bold text-brand-navy mb-3 relative z-10">{title}</h4>
              <p className="text-slate-600 mb-8 flex-grow leading-relaxed font-medium relative z-10">{description}</p>
              <div className="inline-flex items-center gap-2 text-sm font-bold text-brand-navy group-hover:text-brand-green transition-colors mt-auto relative z-10">
                Learn More <SafeIcon icon={FiIcons.FiArrowRight} />
              </div>
            </Link>
          ))}
          <div className="bg-brand-navy rounded-3xl p-8 shadow-md flex flex-col h-full justify-center text-center relative overflow-hidden">
            <div className="absolute inset-0 opacity-20 bg-cover bg-center mix-blend-overlay" style={{backgroundImage: `url(${mediaBase}c0d2875965ab4bd00006ae3a1c3cffae.jpg)`}} />
            <div className="relative z-10">
              <div className="w-16 h-16 bg-brand-green/20 text-brand-green rounded-full flex items-center justify-center mx-auto mb-6">
                <SafeIcon icon={FiIcons.FiCheckCircle} className="text-3xl" />
              </div>
              <h4 className="text-2xl font-bold text-white mb-3">Need a Comprehensive Program?</h4>
              <p className="text-slate-300 mb-8 font-medium">Let&apos;s discuss how we can integrate planning, training, and exercises for your facility.</p>
              <Link to="/consultation" className="inline-flex items-center justify-center w-full gap-2 px-6 py-4 bg-brand-green text-white font-bold rounded-xl hover:bg-green-600 transition-colors shadow-lg shadow-brand-green/20">
                Schedule Consultation
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Leadership() {
  return (
    <section className="py-24 bg-brand-navy text-white overflow-hidden relative">
      <div className="absolute top-0 right-0 w-[800px] h-[800px] bg-brand-green/5 rounded-full blur-3xl -mr-40 -mt-40 pointer-events-none" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid lg:grid-cols-2 gap-16 lg:gap-24 items-center">
          <div>
            <h2 className="text-brand-green font-bold tracking-widest uppercase text-xs mb-3">Our Leaders</h2>
            <h3 className="text-3xl md:text-5xl font-bold mb-6 tracking-tight">Experience that bridges the gap between theory and reality.</h3>
            <p className="text-lg text-slate-300 font-medium mb-10 leading-relaxed">
              Terry and Linda Boes built Boes &amp; Boes together, bringing decades of combined experience in high-stakes emergency management and organizational talent development.
            </p>
            <div className="space-y-8">
              <div className="flex gap-5 items-start bg-white/5 p-6 rounded-2xl border border-white/10 hover:border-brand-green/50 transition-colors">
                <div className="w-12 h-12 bg-brand-green/20 text-brand-green rounded-full flex items-center justify-center shrink-0"><SafeIcon icon={FiIcons.FiAward} className="text-xl" /></div>
                <div>
                  <h4 className="font-bold text-xl mb-1 text-white flex flex-wrap items-center gap-2">Terry Boes <span className="text-xs font-bold bg-white/10 px-2 py-1 rounded text-brand-green tracking-wider">MBA, CEM, CFPS, CSP</span></h4>
                  <p className="text-slate-400 text-sm leading-relaxed mt-2">27 years as a firefighter &amp; hazmat specialist. Instructor for FEMA and the U.S. National Fire Academy.</p>
                </div>
              </div>
              <div className="flex gap-5 items-start bg-white/5 p-6 rounded-2xl border border-white/10 hover:border-brand-green/50 transition-colors">
                <div className="w-12 h-12 bg-brand-green/20 text-brand-green rounded-full flex items-center justify-center shrink-0"><SafeIcon icon={FiIcons.FiUsers} className="text-xl" /></div>
                <div>
                  <h4 className="font-bold text-xl mb-1 text-white flex flex-wrap items-center gap-2">Linda Boes <span className="text-xs font-bold bg-white/10 px-2 py-1 rounded text-brand-green tracking-wider">CEO, MS Ind. Psychology</span></h4>
                  <p className="text-slate-400 text-sm leading-relaxed mt-2">Talent development &amp; leadership specialist. CPLP certified with deep experience in organizational design.</p>
                </div>
              </div>
            </div>
            <div className="mt-10">
              <Link to="/about" className="inline-flex items-center gap-2 px-8 py-4 rounded-xl bg-white/10 hover:bg-white/20 font-bold transition-all border border-white/20 hover:shadow-lg">Read Our Full Story</Link>
            </div>
          </div>
          <div className="relative h-[500px] hidden md:block">
            <motion.div initial={{opacity: 0, x: 30}} whileInView={{opacity: 1, x: 0}} viewport={{once: true}} transition={{duration: 0.6}} className="absolute top-0 right-0 w-[80%] h-[320px] rounded-3xl overflow-hidden shadow-2xl border-4 border-brand-navy z-10">
              <img src="https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1784999355069-blob" alt="Linda Boes" className="w-full h-full object-cover object-top" />
              <div className="absolute inset-0 bg-brand-navy/10 mix-blend-multiply" />
            </motion.div>
            <motion.div initial={{opacity: 0, y: 30}} whileInView={{opacity: 1, y: 0}} viewport={{once: true}} transition={{duration: 0.6, delay: 0.2}} className="absolute bottom-0 left-0 w-[70%] h-[280px] rounded-3xl overflow-hidden shadow-2xl border-4 border-brand-navy z-20">
              <img src="https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1784999010215-blob" alt="Terry Boes" className="w-full h-full object-cover object-top" />
              <div className="absolute inset-0 bg-brand-navy/10 mix-blend-multiply" />
            </motion.div>
          </div>
          <div className="md:hidden mt-10 grid grid-cols-2 gap-4">
            {[
              ['Terry Boes', 'https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1784999010215-blob'],
              ['Linda Boes', 'https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1784999355069-blob']
            ].map(([name, src]) => (
              <div key={name} className="rounded-3xl overflow-hidden shadow-xl border-4 border-brand-navy aspect-[4/5]">
                <img src={src} alt={name} className="w-full h-full object-cover object-top" />
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function LocalFocus() {
  return (
    <section className="py-24 bg-white relative overflow-hidden">
      <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[1000px] h-[1000px] bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-brand-gray/10 via-transparent to-transparent opacity-50 pointer-events-none" />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
        <motion.div initial={{opacity: 0, scale: 0.9}} whileInView={{opacity: 1, scale: 1}} viewport={{once: true}} transition={{duration: 0.5}}>
          <div className="w-20 h-20 bg-brand-light shadow-lg rounded-2xl flex items-center justify-center mx-auto mb-8 border border-slate-200">
            <SafeIcon icon={FiIcons.FiMapPin} className="text-brand-green text-3xl" />
          </div>
          <h3 className="text-3xl md:text-4xl font-bold text-brand-navy mb-6 tracking-tight">Proudly Based in Fort Wayne, Indiana</h3>
          <p className="text-xl text-slate-600 leading-relaxed font-medium mb-10">
            After working across more than 30 countries and serving global industrial giants, we returned to Fort Wayne to be closer to family. Today, we bring international-level emergency management and consulting directly to the local business community in the Midwest, while continuing to serve our clients worldwide.
          </p>
          <Link to="/contact" className="inline-flex items-center gap-2 font-bold text-brand-navy hover:text-brand-green transition-colors text-lg">
            Contact our Fort Wayne Office <SafeIcon icon={FiIcons.FiArrowRight} />
          </Link>
        </motion.div>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <div className="w-full bg-brand-light">
      <Hero />
      <LogoCarousel />
      <Challenges />
      <Services />
      <Leadership />
      <LocalFocus />
    </div>
  );
}